import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isEqual } from 'lodash'
import { Box as SubHeaderContainer } from '@node-space/storybook-components/dist/Box'
import { Button } from '@node-space/storybook-components/dist/Button'
import Callout from '@node-space/storybook-components/dist/Callout'
import { Icon } from '@node-space/storybook-components/dist/Icon'
import { SkeletonForm } from '@node-space/storybook-components/dist/SkeletonLoader'
import { logSentryErrorAndGetTraceId } from '@node-space/utils'
import { Description } from 'components/Text'
import { useVerificationContext } from 'hooks/context/useVerificationContext'
import { useBusinessOwnersMutation } from 'hooks/mutations/useBusinessOwnersMutation'
import { useBusinessOwnersQuery } from 'hooks/queries/useBusinessOwnersQuery'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { BaseErrorResponse } from 'types/errors'
import { BusinessOwnerSubmitData, OwnershipOptions, OwnerTypeOptions } from 'types/verification'
import { FormBody } from '../_components/FormBody'
import { FormGetError } from '../_components/FormGetError'
import { FormSubmitButton } from '../_components/FormSubmitButton'
import { FormSubmitError } from '../_components/FormSubmitError'
import { NameTile, NameTilesContanier } from '../_components/NameTile'
import { VerificationHeader } from '../_components/VerificationHeader'
import { CorporateOwnerReq, IndividualOwnerReq } from '../schemas/businessOwnersSchema'
import { BusinessOwnerModal, OwnerModalData } from './BusinessOwnerModal'

const emptyIndividualForm: IndividualOwnerReq = {
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  address1: '',
  address2: '',
  city: '',
  postalCode: '',
  individualCountryCode: '',
}

const emptyCorporateForm: CorporateOwnerReq = {
  businessName: '',
  businessCountryCode: '',
  registrationNumber: '',
  ownershipPercentage: '',
  ownershipType: OwnershipOptions.DIRECT,
}

const emptyOwnerModalData: OwnerModalData = {
  id: null,
  ownerType: OwnerTypeOptions.INDIVIDUAL,
  ...emptyIndividualForm,
  ...emptyCorporateForm,
}

export const TEMP_ID_TAG = 'tempId - '

export const BusinessOwners = () => {
  const { t } = useTranslation()

  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()
  const { setUnsavedChanges, submitStep } = useVerificationContext()

  const [initialOwners, setInitialOwners] = useState<OwnerModalData[]>([])
  const [businessOwners, setBusinessOwners] = useState<OwnerModalData[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isEditingOwner, setIsEditingOwner] = useState(false)
  const [isEmptyError, setIsEmptyError] = useState(false)
  const [selectedOwnerData, setSelectedOwnerData] = useState<OwnerModalData>(emptyOwnerModalData)

  const {
    data: businessOwnersData,
    isFetching: isFetchingBusinessOwners,
    isError: isGetError,
  } = useBusinessOwnersQuery()

  const { mutate: submitOwners, isLoading: isLoadingSubmitRequest } = useBusinessOwnersMutation()

  const setLoadedOwners = () => {
    const loadedOwners: OwnerModalData[] = [...businessOwnersData]?.map(ownerData => {
      const { registrationNumber } = ownerData
      const isIndividual = !!ownerData?.firstName
      return {
        id: ownerData?.id,
        ownerType: isIndividual ? OwnerTypeOptions.INDIVIDUAL : OwnerTypeOptions.CORPORATE,
        firstName: ownerData?.firstName ?? '',
        lastName: ownerData?.lastName ?? '',
        dateOfBirth: ownerData?.dateOfBirth ?? '',
        address1: ownerData?.address1 ?? '',
        address2: ownerData?.address2 ?? '',
        city: ownerData?.city ?? '',
        postalCode: ownerData?.postalCode ?? '',
        individualCountryCode: isIndividual ? ownerData?.countryCode : '',
        businessCountryCode: !isIndividual ? ownerData?.countryCode : '',
        businessName: ownerData?.name ?? '',
        registrationNumber: registrationNumber ? registrationNumber.toLocaleString() : '',
        ownershipPercentage: ownerData?.ownershipPercentage ?? '',
        ownershipType: ownerData?.ownershipType ?? OwnershipOptions.DIRECT,
      }
    })
    setInitialOwners(loadedOwners)
    setBusinessOwners(loadedOwners)
  }

  const resetModalData = () => {
    setIsEditingOwner(false)
    setSelectedOwnerData(emptyOwnerModalData)
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    resetModalData()
  }

  const onSaveOwner = (savedOwnerData: OwnerModalData) => {
    if (isEmptyError) {
      setIsEmptyError(false)
    }

    let updatedOwners
    setBusinessOwners(currentOwners => {
      if (!isEditingOwner) {
        updatedOwners = [...currentOwners, savedOwnerData]
      } else {
        updatedOwners = [...currentOwners]
        const editedOwnerIndex = updatedOwners.findIndex(owner => owner?.id === savedOwnerData.id)
        updatedOwners[editedOwnerIndex] = savedOwnerData
      }
      return updatedOwners
    })
    handleModalClose()
  }

  const onEditOwner = useCallback(
    ownerId => {
      const ownerIndex = businessOwners.findIndex(owner => owner.id === ownerId)
      setSelectedOwnerData(businessOwners[ownerIndex])
      setIsEditingOwner(true)
      setIsModalOpen(true)
    },
    [businessOwners]
  )

  const onDeleteOwner = useCallback(
    ownerId => {
      const updatedList = [...businessOwners].filter(owner => owner.id !== ownerId)
      setBusinessOwners(updatedList)
      if (isEditingOwner) {
        handleModalClose()
      }
    },
    [businessOwners, isEditingOwner]
  )

  const isTempId = (id: string | number): boolean =>
    typeof id === 'string' && id.includes(TEMP_ID_TAG)

  const onSubmitOwners = () => {
    resetRequestError()

    if (!businessOwners?.length) {
      setIsEmptyError(true)
      return
    }

    const data: BusinessOwnerSubmitData = {}

    data.owners = [...businessOwners]?.map(
      ({
        id,
        ownerType,
        firstName,
        lastName,
        dateOfBirth,
        address1,
        address2,
        city,
        postalCode,
        individualCountryCode,
        businessCountryCode,
        businessName,
        registrationNumber,
        ownershipPercentage,
        ownershipType,
      }) => {
        return {
          ...(!isTempId(id) && { id: Number(id) }),
          ownerType,
          firstName,
          lastName,
          dateOfBirth,
          address1,
          address2,
          city,
          postalCode,
          countryCode:
            ownerType === OwnerTypeOptions.INDIVIDUAL ? individualCountryCode : businessCountryCode,
          name: businessName,
          registrationNumber,
          ownershipPercentage: !!ownershipPercentage ? parseInt(ownershipPercentage) : null,
          ownershipType,
        }
      }
    )

    submitOwners(data, {
      onSuccess: () => {
        submitStep('Business owners', 'businessDirectors')
      },
      onError: (error: BaseErrorResponse) => {
        const sentryTraceId = logSentryErrorAndGetTraceId('submitOwners', error)
        setRequestError({
          errorCode: error?.status,
          sentryTraceId,
          show: true,
        })
      },
    })
  }

  useEffect(() => {
    if (!isFetchingBusinessOwners && !!businessOwnersData?.length) {
      setLoadedOwners()
    }
  }, [isFetchingBusinessOwners])

  useEffect(() => {
    if (!isFetchingBusinessOwners) {
      const isChanged = !isEqual(initialOwners, businessOwners)
      setUnsavedChanges(isChanged)
    }
  }, [isFetchingBusinessOwners, initialOwners, businessOwners])

  return (
    <>
      <VerificationHeader header={t('verification.routes.businessOwners')} />
      <SubHeaderContainer flex flexCol gapY={16} paddingT={16}>
        <Description>{t('verification.businessOwnersSubHeader1')}</Description>
        <Description>{t('verification.businessOwnersSubHeader2')}</Description>
      </SubHeaderContainer>

      {isFetchingBusinessOwners ? (
        <SkeletonForm name="Business owners" rowCount={1} />
      ) : isGetError ? (
        <FormGetError />
      ) : (
        <FormBody>
          {requestError?.show && (
            <FormSubmitError
              errorCode={requestError?.errorCode}
              sentryTraceId={requestError?.sentryTraceId}
            />
          )}

          {!!businessOwners?.length && (
            <NameTilesContanier>
              {businessOwners?.map(({ id, ownerType, firstName, lastName, businessName }) => (
                <NameTile
                  key={id}
                  tileId={id}
                  label={
                    ownerType === OwnerTypeOptions.INDIVIDUAL
                      ? `${firstName} ${lastName}`
                      : `${businessName}`
                  }
                  onEdit={onEditOwner}
                  onDelete={onDeleteOwner}
                />
              ))}
            </NameTilesContanier>
          )}

          {isEmptyError && (
            <Callout
              message={t('verification.businessOwners.noAddedOwners')}
              state="error"
              showCloseButton={false}
            />
          )}

          <Button
            secondary
            iconElement={<Icon name="PlusIcon" color="primary-500" />}
            onClick={() => setIsModalOpen(true)}
            className="w-fit"
          >
            {`${t('addBeneficialOwner')}`}
          </Button>

          <FormSubmitButton
            loading={isLoadingSubmitRequest}
            disabled={isEmptyError}
            onClick={onSubmitOwners}
          />
        </FormBody>
      )}

      {isModalOpen && (
        <BusinessOwnerModal
          ownerData={selectedOwnerData}
          isEditingOwner={isEditingOwner}
          onSaveOwner={onSaveOwner}
          onDeleteOwner={onDeleteOwner}
          onClose={handleModalClose}
        />
      )}
    </>
  )
}

export default BusinessOwners
